import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldOff, HeartOff, Shield, Plus, RotateCcw, Trash2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

const ICONS = { ban: ShieldOff, 'heart-off': HeartOff, shield: Shield };
const MILESTONES = [1, 7, 14, 30, 50, 60, 90, 100, 150, 180, 200, 250, 300, 365, 500, 1000];
const todayISO = () => new Date().toISOString().slice(0, 10);
const nextMilestone = (d) => MILESTONES.find((m) => m > d);

export const Commitments = () => {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => nexaApi.get('/commitments').then(({ data }) => setItems(data.commitments)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = async (c) => {
    await nexaApi.patch(`/commitments/${c.id}`, { start_date: todayISO() });
    toast.success('Counter reset to day 0');
    setOpenId(null); load();
  };
  const remove = async (c) => {
    await nexaApi.delete(`/commitments/${c.id}`);
    setOpenId(null); load();
  };
  const add = async () => {
    const label = window.prompt('New commitment (e.g. "No sugar"):');
    if (!label || !label.trim()) return;
    await nexaApi.post('/commitments', { label: label.trim(), icon: 'shield' });
    load();
  };

  if (!items.length) return null;

  return (
    <div data-testid="nexa-commitments">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono tracking-[0.25em] text-white/40 uppercase">My rules</span>
        <button onClick={add} data-testid="nexa-commitment-add" className="text-white/40 active:scale-90"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((c, i) => {
          const Icon = ICONS[c.icon] || Shield;
          const pct = c.target_days ? Math.min(100, Math.round((c.days / c.target_days) * 100)) : 0;
          const isWin = MILESTONES.includes(c.days) || (c.target_days && c.days === c.target_days);
          const next = c.target_days && c.days < c.target_days ? c.target_days : nextMilestone(c.days);
          return (
            <motion.div key={c.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => setOpenId(openId === c.id ? null : c.id)}
              data-testid={`nexa-commitment-${c.id}`}
              className={`relative shrink-0 min-w-[150px] rounded-2xl border bg-[#0A0A0A] px-3.5 py-3 overflow-hidden cursor-pointer ${
                isWin ? 'border-amber-400/60 shadow-[0_0_22px_rgba(251,191,36,0.28)]' : 'border-rose-400/20'
              }`}>
              <span className={`absolute -top-6 right-0 w-20 h-20 rounded-full blur-2xl pointer-events-none ${isWin ? 'bg-amber-400/25' : 'bg-rose-500/15'}`} />
              {isWin && (
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/50 text-amber-300 text-[9px] font-semibold"
                  data-testid={`nexa-commitment-win-${c.id}`}>
                  <Trophy className="w-2.5 h-2.5" /> {c.days === c.target_days ? 'GOAL!' : `${c.days}-DAY WIN`}
                </motion.span>
              )}
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`w-4 h-4 ${isWin ? 'text-amber-300' : 'text-rose-300'}`} strokeWidth={2} />
                <span className="text-[12px] font-semibold text-white/90 truncate">{c.label}</span>
              </div>
              <div className="flex items-end gap-1">
                <span className={`text-[22px] font-bold leading-none ${isWin ? 'text-amber-300' : 'text-white'}`} data-testid={`nexa-commitment-days-${c.id}`}>{c.days}</span>
                <span className="text-[11px] text-white/45 mb-0.5">{c.target_days ? `/ ${c.target_days} days` : c.days === 1 ? 'day' : 'days'}</span>
              </div>
              {c.target_days > 0 ? (
                <div className="h-1 rounded-full bg-white/[0.08] mt-2 overflow-hidden">
                  <div className={`h-full rounded-full ${isWin ? 'bg-amber-400' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
                </div>
              ) : next ? (
                <p className="text-[9.5px] text-white/35 mt-1.5">{next - c.days} day{next - c.days === 1 ? '' : 's'} to {next}</p>
              ) : null}
              {openId === c.id && (
                <div className="flex gap-1.5 mt-2.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => reset(c)} data-testid={`nexa-commitment-reset-${c.id}`}
                    className="flex-1 h-7 rounded-lg border border-white/10 text-white/60 text-[10px] flex items-center justify-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>
                  <button onClick={() => remove(c)} data-testid={`nexa-commitment-del-${c.id}`}
                    className="w-7 h-7 rounded-lg border border-white/10 text-rose-300/60 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
